import { ChipTone } from "./index";

/**
 * Shared stress / percentile color ramp — CIO-approved 2026-06-16.
 *
 * ONE direction, hotter = worse, applied to EVERY percentile / stress reading
 * across the app so a rising stress level never reads as reassuring. This
 * replaces the old gold-as-"mid" tone (the Market Snapshot confusion).
 *
 *   < 60th  → calm     (cool / neutral)
 *   60–79th → elevated (amber)
 *   80–94th → high     (orange)
 *   95th+   → extreme  (red)
 *
 * Direction (up/down) must be shown with an arrow/sign, NOT these colors.
 */

export type StressBand = "calm" | "elevated" | "high" | "extreme";

// Accepts a percentile as either a 0–1 fraction or a 0–100 number.
export function stressBandForPercentile(pct?: number | null): StressBand | null {
  if (pct == null || Number.isNaN(pct)) return null;
  const p = pct <= 1 ? pct * 100 : pct;
  if (p >= 95) return "extreme";
  if (p >= 80) return "high";
  if (p >= 60) return "elevated";
  return "calm";
}

export const STRESS_BAND_TONE: Record<StressBand, ChipTone> = {
  calm: "neutral", // cool — within normal operating range
  elevated: "warning", // amber — notably elevated (60–79th)
  high: "elevated", // orange — high (80–94th)
  extreme: "danger", // red — extreme (95th+)
};

export const STRESS_BAND_LABEL: Record<StressBand, string> = {
  calm: "Calm",
  elevated: "Elevated",
  high: "High",
  extreme: "Extreme",
};

// Theme-stable hex for charts / Plotly markers where Tailwind classes don't reach.
export const STRESS_BAND_HEX: Record<StressBand, string> = {
  calm: "#64748b", // slate
  elevated: "#f59e0b", // amber
  high: "#f97316", // orange
  extreme: "#ef4444", // red
};

export const stressToneForPercentile = (pct?: number | null): ChipTone => {
  const band = stressBandForPercentile(pct);
  return band ? STRESS_BAND_TONE[band] : "neutral";
};

export const stressHexForPercentile = (pct?: number | null): string => {
  const band = stressBandForPercentile(pct);
  return band ? STRESS_BAND_HEX[band] : STRESS_BAND_HEX.calm;
};
